import { useEffect, useMemo, useRef } from "react";

import { Lock } from "lucide-react";

import { Dialog, DialogContent, DialogTitle } from "@fluid-app/ui-primitives";

/** Display strings for the modal chrome — injected by the host app so the
 *  package stays translation-system agnostic. */
export interface VgsThreeDSModalLabels {
  title: string;
  cancel: string;
  timedOut: string;
  stillWorking: string;
  dontRefresh: string;
}

export interface VgsThreeDSModalProps {
  isOpen: boolean;
  challengeHtml: string;
  challengeUrl?: string;
  onClose: (error?: string) => void;
  labels: VgsThreeDSModalLabels;
}

function escapeAttr(s: string | null): string {
  return (s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * Parse the challenge HTML to extract the form action URL and hidden fields.
 * This lets us POST directly to the bank ACS, bypassing intermediary pages
 * (e.g. Entersekt's red.js auto-submit) that cause extra iframe loads.
 */
function parseDirectPostHtml(
  challengeHtml: string,
  challengeUrl?: string,
): string | null {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(challengeHtml, "text/html");
    const form = doc.querySelector("form");
    if (!form) return null;

    const action = challengeUrl || form.getAttribute("action");
    if (!action) return null;

    const hiddenInputs = form.querySelectorAll('input[type="hidden"]');
    if (hiddenInputs.length === 0) return null;

    const fieldsHtml = Array.from(hiddenInputs)
      .map(
        (input) =>
          `<input type="hidden" name="${escapeAttr(input.getAttribute("name"))}" value="${escapeAttr(input.getAttribute("value"))}">`,
      )
      .join("\n");

    return `<!DOCTYPE html>
<html>
<body>
<form id="acsForm" method="POST" action="${escapeAttr(action)}">
${fieldsHtml}
</form>
<script>document.getElementById("acsForm").submit();</script>
</body>
</html>`;
  } catch {
    return null;
  }
}

/**
 * Pure iframe container for the 3DS challenge.
 *
 * The modal does NOT try to detect challenge completion via load-counting or
 * postMessage — that was fragile across different bank ACS implementations.
 * Instead, `useVgs3DSCompletion` subscribes to the realtime transport as soon
 * as the modal opens and receives the authoritative result from the backend.
 *
 * This modal only handles:
 * - Rendering the ACS challenge iframe
 * - User cancellation (Cancel button in header band)
 * - Hard timeout (6.5 min) as a safety net
 */
export function VgsThreeDSModal({
  isOpen,
  challengeHtml,
  challengeUrl,
  onClose,
  labels,
}: VgsThreeDSModalProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const onCloseRef = useRef(onClose);
  // Capture labels in a ref so the timeout effect can read the current
  // translation without depending on labels in its deps array (host apps
  // typically build the labels object inline per render and would otherwise
  // reset the timer on every re-render).
  const labelsRef = useRef(labels);

  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    labelsRef.current = labels;
  });

  // Build a direct-POST page that skips intermediary auto-redirect pages.
  // Falls back to the original challengeHtml if parsing fails.
  const iframeSrcDoc = useMemo(
    () => parseDirectPostHtml(challengeHtml, challengeUrl) ?? challengeHtml,
    [challengeHtml, challengeUrl],
  );

  // Hard timeout — aligned just past the backend safety-net boundary (~6:00)
  // so the cart poll surfaces the backend's verdict (accurate decline,
  // rescued approval, or TIMEOUT) before the modal gives up at 6.5 min.
  // (The old comment attributed the 5-minute value to the EMVCo spec;
  // EMVCo leaves challenge-timeout handling to the ACS/issuer — the real
  // constraint is our own safety-net boundary.) Depends only on isOpen so
  // the timer resets on open, not on content change.
  useEffect(() => {
    if (!isOpen) return;

    const timeoutId = setTimeout(() => {
      onCloseRef.current(labelsRef.current.timedOut);
    }, 390_000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isOpen]);

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      {/* z-[110]: a bank challenge must never be occluded by host chrome.
          Card-add surfaces launch this modal from a dialog that pins its
          content at z-[100] (fluid-checkout's credit-card dialog), where the
          default z-50 + DOM-order stacking would paint the host dialog on
          top of the challenge — and Radix would inert it underneath. */}
      <DialogContent
        className="z-[110] h-[600px] max-w-2xl p-0 sm:max-w-2xl"
        overlayClassName="z-[110]"
        showCloseButton={false}
      >
        <div className="flex h-full w-full flex-col">
          {/* Header band — replaces VisuallyHidden DialogTitle */}
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-gray-600" aria-hidden="true" />
              <DialogTitle className="text-sm font-semibold text-gray-900">
                {labels.title}
              </DialogTitle>
            </div>
            <button
              type="button"
              onClick={handleCancel}
              className="rounded text-xs text-indigo-600 hover:text-indigo-700 focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 focus-visible:outline-none"
            >
              {labels.cancel}
            </button>
          </div>

          {/* Iframe (unchanged behavior). */}
          <div className="relative flex-1">
            {challengeHtml && (
              <iframe
                ref={iframeRef}
                srcDoc={iframeSrcDoc}
                className="h-full w-full border-0"
                title="VGS 3DS Challenge"
                sandbox="allow-same-origin allow-scripts allow-forms allow-top-navigation allow-popups"
              />
            )}
          </div>

          {/* Footer band — indeterminate progress + status caption + tab-refresh hint */}
          <div className="border-t border-gray-200 px-4 py-3">
            <div
              data-testid="threeds-progress-bar"
              role="progressbar"
              aria-label={labels.stillWorking}
              className="relative h-1 overflow-hidden rounded-full bg-indigo-100"
            >
              <div className="threeds-progress-pill absolute top-0 h-full w-[30%] rounded-full bg-indigo-500" />
            </div>
            <div className="mt-2 flex items-center justify-between text-xs">
              <span className="text-gray-600">{labels.stillWorking}</span>
              <span className="text-gray-400">{labels.dontRefresh}</span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default VgsThreeDSModal;
